import { deckFor, cardById } from './simulationEngine';

/**
 * Client-side mirror of backend/src/utils/simulationSerializer.forParticipant().
 *
 * Used ONLY by the trainer's preview screen, which holds a full simulation and needs to
 * render the participant component without a live run. In an actual session the view
 * arrives from the server already redacted, and this function is not involved.
 *
 * It exists so `SimulationActivity` has exactly one prop contract — the redacted view —
 * rather than one shape in preview and another in production. If those diverged, the
 * preview would stop being an honest picture of what participants see, which is the
 * only reason the preview exists.
 */
export const toParticipantView = (simulation, myFunctionKey, { roundIndex = null } = {}) => {
  if (!simulation || !myFunctionKey) return null;

  return {
    meta: {
      title: simulation.meta?.title,
      industry: simulation.meta?.industry,
      functionNoun: simulation.meta?.functionNoun,
      eventDeckLabel: simulation.meta?.eventDeckLabel,
    },
    kpis: (simulation.kpis || []).map((k) => ({
      key: k.key, label: k.label, unit: k.unit, direction: k.direction, variant: k.variant,
    })),
    functions: (simulation.functions || []).map((f) => ({
      key: f.key,
      name: f.name,
      variant: f.variant,
      mandate: f.mandate,
      publicBrief: f.publicBrief,
      // Only your own. Everyone else's private brief is what makes the silo real.
      privateBrief: f.key === myFunctionKey ? f.privateBrief : undefined,
    })),
    rounds: (simulation.rounds || [])
      .filter((r) => roundIndex === null || r.index === roundIndex)
      .map((r) => ({
        index: r.index,
        title: r.title,
        context: r.context,
        conflict: r.conflict || [],
        challenge: r.challenge,
        decisionTensions: r.decisionTensions || [],
        timeboxSec: r.timeboxSec,
        negotiateSec: r.negotiateSec,
      })),
    myFunctionKey,
    // Title and description only — never whenChosen, leadershipAngle, behaviour or kpi.
    deck: deckFor(simulation, myFunctionKey).map((c) => ({
      id: c.id, functionKey: c.functionKey, order: c.order, title: c.title, description: c.description,
    })),
    reveal: null,
  };
};

/**
 * Build a reveal block for the preview, matching forParticipantReveal(). Like the
 * server, it never includes the insight on a card — that waits for the final debrief.
 */
export const toParticipantReveal = (simulation, resolved) => {
  if (!simulation || !resolved) return null;
  return {
    roundIndex: resolved.roundIndex,
    attribution: (resolved.perFunction || []).map((p) => {
      const card = cardById(simulation, p.cardId);
      return {
        functionKey: p.functionKey,
        functionName: simulation.functions.find((f) => f.key === p.functionKey)?.name,
        card: card
          ? {
              id: card.id, functionKey: card.functionKey, order: card.order,
              title: card.title, description: card.description,
            }
          : null,
        kpi: p.kpi,
      };
    }),
    event: resolved.eventCardId
      ? simulation.eventCards.find((e) => e.id === resolved.eventCardId) || null
      : null,
    total: resolved.total,
    cumulative: resolved.cumulative,
  };
};

export default toParticipantView;
